import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {CollapsibleSidebarLinkItems, ReposShortcutViewsGroup} from '@github-ui/repos-list'
import type {SidebarLink} from '@github-ui/saved-views'

const sidebarLinks: SidebarLink[] = [
  {
    id: 'contributed',
    name: 'My contributions',
    searchType: 'repositories',
    query: 'contributed-by:@me',
    icon: 'people',
    color: 'gray',
    source: 'predefined',
  },
  {
    id: 'owned',
    name: 'My repositories',
    searchType: 'repositories',
    query: 'owner:@me',
    icon: 'person',
    color: 'gray',
    source: 'predefined',
  },
  {
    id: 'forks',
    name: 'My forks',
    searchType: 'repositories',
    query: 'owner:@me fork:true',
    icon: 'repo_forked',
    color: 'gray',
    source: 'predefined',
  },
  {
    id: 'adminable',
    name: 'Admin access',
    searchType: 'repositories',
    query: 'admin:@me',
    icon: 'gear',
    color: 'gray',
    source: 'predefined',
  },
  {
    id: 'internal',
    name: 'Internal',
    searchType: 'repositories',
    query: 'visibility:internal archived:false',
    icon: 'organization',
    color: 'gray',
    source: 'predefined',
  },
  {
    id: 'private',
    name: 'Private',
    searchType: 'repositories',
    query: 'visibility:private archived:false',
    icon: 'repo_locked',
    color: 'gray',
    source: 'predefined',
  },
]

interface CollapsibleFinderSidebarProps {
  selectedLink: string
  onSelectLink: (linkItem: SidebarLink) => void
  shortcuts: SidebarLink[]
  onCreateView: () => void
}

/**
 * Renders the repository-type links and the saved "Views" group inside a
 * single `CollapsibleNavList` so the group divider separates the two sections
 * in both expanded and collapsed states instead of bridging two separate lists
 * with a padding hack.
 */
export function CollapsibleFinderSidebar({
  selectedLink,
  onSelectLink,
  shortcuts,
  onCreateView,
}: CollapsibleFinderSidebarProps) {
  return (
    <CollapsibleNavList aria-label="Repositories">
      <CollapsibleSidebarLinkItems links={getSidebarLinks()} selectedLink={selectedLink} onSelectLink={onSelectLink} />
      <ReposShortcutViewsGroup
        shortcuts={shortcuts}
        selectedLink={selectedLink}
        onSelectLink={onSelectLink}
        onCreateView={onCreateView}
      />
    </CollapsibleNavList>
  )
}

export function getItemFromQuery(query: string, shortcuts: SidebarLink[]) {
  const allUserLinks = [...sidebarLinks, ...shortcuts]
  return allUserLinks.find(item => item.query === query.trim())
}

export function getItemById(id: string, shortcuts: SidebarLink[]) {
  if (!id) return undefined

  const allUserLinks = [...sidebarLinks, ...shortcuts]
  return allUserLinks.find(item => item.id === id)
}

function getSidebarLinks() {
  const showInternalPrivate = isFeatureEnabled('repos_finder_unbounded')
  if (showInternalPrivate) {
    return sidebarLinks
  }

  return sidebarLinks.filter(link => !['internal', 'private'].includes(link.id))
}
